import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { RefreshCw, TrendingUp, TrendingDown, Anchor, Layers, Sparkles, Check } from "lucide-react";

const fmtNum = (n, d = 5) => (n === null || n === undefined ? "—" : Number(n).toFixed(d).replace(/0+$/, "").replace(/\.$/, ""));
const fmtMoney = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

const DirTag = ({ dir }) => {
  const isBuy = dir === "BUY";
  const c = isBuy ? "#10b981" : "#ef4444";
  const bg = isBuy ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ background: bg, borderColor: c, color: c }}>
      {isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {dir}
    </span>
  );
};

// Phase 2.1 — TM state pills. Renders the current TM state for a position.
// The events list drives the individual pills; if none present but tm is
// configured we show a grey "TM idle" pill.
const TmPills = ({ position, tmConfigured, events }) => {
  const beDone = events.some((e) => e.event_type === "break_even_applied");
  const trailingHit = events.some((e) => e.event_type === "trailing_updated");
  const trailingLast = [...events].reverse().find((e) => e.event_type === "trailing_updated");
  const partialLevels = events.filter((e) => (e.event_type || "").startsWith("partial_close_")).length;

  const pills = [];
  if (beDone) pills.push({ testId: "pill-be-done", cls: "border-[#10b981] text-[#10b981] bg-[#10b981]/10", icon: <Check className="w-3 h-3" strokeWidth={3} />, label: "BE ✓" });
  if (trailingHit) pills.push({ testId: "pill-trailing", cls: "border-[#3b82f6] text-[#3b82f6] bg-[#3b82f6]/10", icon: <TrendingDown className="w-3 h-3" />, label: `TS ${trailingLast?.sl_after ? "↗" : "active"}` });
  if (partialLevels > 0) pills.push({ testId: "pill-partial", cls: "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10", icon: <Layers className="w-3 h-3" />, label: `PC ${partialLevels}/3` });
  if (pills.length === 0 && tmConfigured) pills.push({ testId: "pill-tm-idle", cls: "border-[#374151] text-[#6b7280] bg-transparent", icon: <Sparkles className="w-3 h-3" />, label: "TM idle" });

  if (pills.length === 0) return <span className="text-[10px] text-[#6b7280] font-mono">—</span>;

  const tooltip = events.length
    ? `VEEROX INTEL AI · managed position\n\n` + events.map((e) => `[${fmtTime(e.created_at)}] ${e.event_type}${e.level ? ` L${e.level}` : ""}`).join("\n")
    : "VEEROX INTEL AI · no TM events yet";

  return (
    <div className="flex items-center gap-1 flex-wrap" title={tooltip} data-testid={`tm-pills-${position.ticket}`}>
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono font-bold border border-[#a78bfa]/40 text-[#a78bfa]/80 bg-[#a78bfa]/5" data-testid={`vx-brand-${position.ticket}`}>
        <Sparkles className="w-2 h-2" /> VX·AI
      </span>
      {pills.map((p) => (
        <span key={p.testId} data-testid={p.testId} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border ${p.cls}`}>
          {p.icon}{p.label}
        </span>
      ))}
    </div>
  );
};

export default function Portfolio() {
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState({ trades: [], wins: 0, losses: 0, win_rate_pct: 0, total_pnl: 0 });
  const [curve, setCurve] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsByTicket, setEventsByTicket] = useState({});
  const [onlyActiveTm, setOnlyActiveTm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h, c, ev] = await Promise.all([
        api.get("/portfolio/positions"),
        api.get("/portfolio/history?days=30"),
        api.get("/portfolio/equity-curve?days=30"),
        api.get("/portfolio/tm-events?days=7").catch(() => ({ data: { events: [] } })),
      ]);
      setPositions(p.data.positions || []);
      setHistory(h.data || {});
      setCurve(c.data.points || []);
      const map = {};
      (ev.data.events || []).forEach((e) => {
        map[e.mt5_ticket] = map[e.mt5_ticket] || [];
        map[e.mt5_ticket].push(e);
      });
      setEventsByTicket(map);
    } catch (e) {
      toast.error("Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  useWebSocket((msg) => {
    if (["trade.opened", "trade.updated", "trade.closed", "position.tm_event"].includes(msg.type)) load();
  });

  const visiblePositions = useMemo(() => {
    if (!onlyActiveTm) return positions;
    return positions.filter((p) => (eventsByTicket[p.ticket] || []).length > 0);
  }, [positions, onlyActiveTm, eventsByTicket]);

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vx-label">Portfolio</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Positions &amp; Performance</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-[#1f2937] hover:border-[#374151] cursor-pointer text-xs font-mono text-[#9ca3af]" data-testid="tm-filter-label">
            <input
              type="checkbox"
              checked={onlyActiveTm}
              onChange={(e) => setOnlyActiveTm(e.target.checked)}
              className="accent-[#a78bfa]"
              data-testid="tm-filter-checkbox"
            />
            Active TM only
          </label>
          <button onClick={load} data-testid="portfolio-refresh" className="h-9 px-3 rounded-md border border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono flex items-center gap-2">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="vx-card p-5" data-testid="equity-curve-card">
        <div className="flex items-center justify-between mb-4">
          <div className="vx-label">Equity Curve · 30d</div>
          <div className="text-[11px] text-[#6b7280] font-mono">
            Total P&amp;L: <span className={history.total_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}>{fmtMoney(history.total_pnl)}</span>
            {" · "}Win rate: <span className="text-[#3b82f6]">{history.win_rate_pct?.toFixed(1) || "0.0"}%</span>
          </div>
        </div>
        {curve.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-xs text-[#6b7280] font-mono">
            No snapshot history yet — waiting for EA account-info feed.
          </div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="time" tick={{ fill: "#6b7280", fontSize: 10 }} tickFormatter={(t) => t?.slice(5, 10) || ""} />
                <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} width={60} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", fontSize: 12 }} labelStyle={{ color: "#9ca3af" }} />
                <Line type="monotone" dataKey="equity" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="vx-card overflow-hidden" data-testid="positions-card">
        <div className="px-5 py-3 border-b border-[#1f2937] flex items-center justify-between">
          <div className="vx-label">Open Positions ({visiblePositions.length}{onlyActiveTm ? ` / ${positions.length}` : ""})</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="positions-table">
            <thead className="border-b border-[#1f2937]">
              <tr className="text-left">
                <th className="py-2 px-4 vx-label">Ticket</th>
                <th className="py-2 px-2 vx-label">Symbol</th>
                <th className="py-2 px-2 vx-label">Dir</th>
                <th className="py-2 px-2 vx-label text-right">Lot</th>
                <th className="py-2 px-2 vx-label text-right">Entry</th>
                <th className="py-2 px-2 vx-label text-right">SL</th>
                <th className="py-2 px-2 vx-label text-right">TP</th>
                <th className="py-2 px-2 vx-label">TM state</th>
                <th className="py-2 px-4 vx-label text-right">Floating P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {visiblePositions.length === 0 ? (
                <tr><td colSpan={9} className="px-6 py-8 text-center text-[#6b7280] font-mono text-sm">{onlyActiveTm ? "No positions with TM events." : "No open positions."}</td></tr>
              ) : visiblePositions.map((p) => {
                const events = eventsByTicket[p.ticket] || [];
                return (
                  <tr key={p.ticket} className="border-b border-[#1f2937] hover:bg-[#1f2937]/50" data-testid={`position-${p.ticket}`}>
                    <td className="py-3 px-4 font-mono text-xs text-[#9ca3af]">#{p.ticket}</td>
                    <td className="py-3 px-2 font-mono font-semibold">{p.symbol}</td>
                    <td className="py-3 px-2"><DirTag dir={p.direction} /></td>
                    <td className="py-3 px-2 font-mono text-xs text-right">{p.lot}</td>
                    <td className="py-3 px-2 font-mono text-xs text-right">{fmtNum(p.entry_price)}</td>
                    <td className="py-3 px-2 font-mono text-xs text-right text-[#ef4444]/80">{fmtNum(p.sl)}</td>
                    <td className="py-3 px-2 font-mono text-xs text-right text-[#10b981]/80">{fmtNum(p.tp)}</td>
                    <td className="py-3 px-2"><TmPills position={p} tmConfigured={events.length > 0} events={events} /></td>
                    <td className={`py-3 px-4 font-mono text-xs text-right font-bold ${p.current_pnl >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>{fmtMoney(p.current_pnl)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vx-card overflow-hidden" data-testid="history-card">
        <div className="px-5 py-3 border-b border-[#1f2937] flex items-center justify-between">
          <div className="vx-label">Closed Trades ({history.count || 0})</div>
          <div className="text-[11px] text-[#6b7280] font-mono">Wins {history.wins || 0} · Losses {history.losses || 0}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1f2937]">
              <tr className="text-left">
                <th className="py-2 px-4 vx-label">Closed</th>
                <th className="py-2 px-2 vx-label">Symbol</th>
                <th className="py-2 px-2 vx-label">Dir</th>
                <th className="py-2 px-2 vx-label text-right">Lot</th>
                <th className="py-2 px-2 vx-label text-right">Entry</th>
                <th className="py-2 px-2 vx-label text-right">Exit SL/TP</th>
                <th className="py-2 px-4 vx-label text-right">P&amp;L</th>
                <th className="py-2 px-2 vx-label text-center">Result</th>
              </tr>
            </thead>
            <tbody>
              {(!history.trades || history.trades.length === 0) ? (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-[#6b7280] font-mono text-sm">No closed trades yet.</td></tr>
              ) : history.trades.map((t) => (
                <tr key={t.id} className="border-b border-[#1f2937] hover:bg-[#1f2937]/50">
                  <td className="py-3 px-4 font-mono text-xs text-[#9ca3af]">{t.closed_at ? new Date(t.closed_at).toLocaleString() : "—"}</td>
                  <td className="py-3 px-2 font-mono font-semibold">{t.symbol}</td>
                  <td className="py-3 px-2"><DirTag dir={t.direction} /></td>
                  <td className="py-3 px-2 font-mono text-xs text-right">{t.lot_size}</td>
                  <td className="py-3 px-2 font-mono text-xs text-right">{fmtNum(t.entry_price)}</td>
                  <td className="py-3 px-2 font-mono text-xs text-right text-[#9ca3af]">{fmtNum(t.sl)}/{fmtNum(t.tp)}</td>
                  <td className={`py-3 px-4 font-mono text-xs text-right font-bold ${t.pnl_realized >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>{fmtMoney(t.pnl_realized)}</td>
                  <td className="py-3 px-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${
                      t.state === "closed_win" ? "border-[#10b981] text-[#10b981] bg-[#10b981]/10" : "border-[#ef4444] text-[#ef4444] bg-[#ef4444]/10"
                    }`}>{t.state === "closed_win" ? "WIN" : "LOSS"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
